// src/utils/layouts.jsx
import React from "react";
import { Box } from "@mui/material";

// 1. محتوى الصفحة (Page Content)
// يُستخدم كحاوية رئيسية لكل صفحة لضمان الهوامش والمركزية
export const PageContainer = ({ children, maxWidth = "lg", ...props }) => (
  <Box
    sx={{
      width: "100%",
      maxWidth: (theme) => theme.breakpoints.values[maxWidth] || maxWidth,
      margin: "0 auto",
      padding: 2, // تعادل 20px بناءً على إعدادات spacing في الثيم
      ...props.sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

// 2. قائمة أفقية (Horizontal List)
// scroll: true (يسمح بالسحب الجانبي) | false (ينزل العناصر للسطر التالي)
export const HList = ({ children, scroll = false, gap = 1.5, ...props }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: gap,
      flexWrap: scroll ? "nowrap" : "wrap",
      overflowX: scroll ? "auto" : "visible",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch", // تجربة سكرول ناعمة للموبايل
      paddingBottom: scroll ? 1 : 0, // مساحة لشريط التمرير إن وُجد
      ...props.sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

// 3. قائمة عمودية (Vertical List)
// scroll: true (سكرول عمودي، يتطلب تحديد ارتفاع الأب) | false (تمدد طبيعي)
export const VList = ({ children, scroll = false, gap = 1.5, ...props }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: gap,
      overflowY: scroll ? "auto" : "visible",
      overflowX: "hidden",
      WebkitOverflowScrolling: "touch",
      ...props.sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

// 4. شبكة (Grid)
// scrollType: 'none' | 'x' (جانبي) | 'y' (عمودي) | 'both' (الكل)
export const GridLayout = ({ 
  children, 
  scrollType = "none", 
  columns = "repeat(auto-fill, minmax(200px, 1fr))", // تجاوب تلقائي: عرض الكرت لا يقل عن 200px
  gap = 2, 
  ...props 
}) => {
  // تحديد خصائص السكرول بناءً على النوع الممرر
  const scrollProps = {
    none: { overflow: "visible" },
    x: { overflowX: "auto", overflowY: "hidden", whiteSpace: "nowrap" },
    y: { overflowY: "auto", overflowX: "hidden" },
    both: { overflow: "auto" },
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: scrollType === "x" ? "auto" : columns, // إذا كان السكرول جانبي، نعطل النزول التلقائي
        gridAutoFlow: scrollType === "x" ? "column" : "row", // ترتيب العناصر أفقياً إذا كان السكرول جانبي
        gap: gap,
        padding: 1, // إعطاء مساحة بسيطة للظلال (box-shadow) لكي لا تُقص
        ...scrollProps[scrollType],
        ...props.sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};


// 5. حاوية التوسيط المركزي (Centered Content)
// تقوم بتوسيط المحتوى عمودياً وأفقياً بالكامل (ممتازة للـ Loaders أو الـ Empty States)
export const CenterContent = ({ children, minHeight = "100%", ...props }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column", // لجعل العناصر تترتب فوق بعضها (مثل صورة وتحتها نص)
      justifyContent: "center", // التوسيط العمودي
      alignItems: "center",     // التوسيط الأفقي
      minHeight: minHeight,     // لضمان أخذ المساحة الكافية للتوسيط
      textAlign: "center",      // لتوسيط أي نصوص بداخلها
      width: "100%",
      ...props.sx,
    }}
    {...props}
  >
    {children}
  </Box>
);